import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Space, Spin, Tag, Typography, notification } from 'antd';
import {
  FilePdfOutlined,
  FileWordOutlined,
  PrinterOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { getCellValue, getRecordIds, getTableName } from '../../api/bitable';
import { BitableApiError, type FieldMeta, type TableContext } from '../../api/types';
import { useAppState } from '../../store/hooks';
import { useBilling } from '../../billing/hooks';
import { buildPrintHtml, type PrintRow } from '../../exporters/printRenderer';
import { downloadBlob, exportToWord } from '../../exporters/wordExporter';
import { buildPdfFileName, exportToPdf } from '../../exporters/pdfExporter';
import { formatValue } from '../../utils/formatValue';
import { printHtmlViaIframe } from '../../utils/printHtml';
import styles from './styles.module.css';

interface PreviewPaneProps {
  fieldMetas: FieldMeta[];
}

function describeError(err: unknown): string {
  if (err instanceof BitableApiError) return `[${err.code}] ${err.message}`;
  if (err instanceof Error) return err.message;
  return String(err);
}

async function fetchRows(
  context: TableContext,
  fieldIds: string[],
  range: 'visible' | 'selected' | 'all',
): Promise<PrintRow[]> {
  const recordIds = await getRecordIds(context.tableId, context.viewId, range);
  const rows: PrintRow[] = [];
  for (const recordId of recordIds) {
    const cells: Record<string, unknown> = {};
    const entries = await Promise.all(
      fieldIds.map(async (fieldId) => {
        try {
          const v = await getCellValue(context.tableId, fieldId, recordId);
          return [fieldId, v] as const;
        } catch {
          return [fieldId, null] as const;
        }
      }),
    );
    for (const [k, v] of entries) cells[k] = v;
    rows.push({ recordId, cells });
  }
  return rows;
}

function sortRows(
  rows: PrintRow[],
  fieldMetas: FieldMeta[],
  fieldId: string | null,
  direction: 'asc' | 'desc',
): PrintRow[] {
  if (!fieldId) return rows;
  const meta = fieldMetas.find((m) => m.id === fieldId);
  if (!meta) return rows;
  const dir = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const sa = formatValue(meta, a.cells[fieldId]);
    const sb = formatValue(meta, b.cells[fieldId]);
    const na = Number(sa);
    const nb = Number(sb);
    if (!Number.isNaN(na) && !Number.isNaN(nb) && sa !== '' && sb !== '') {
      return (na - nb) * dir;
    }
    return sa.localeCompare(sb, 'zh-CN') * dir;
  });
}

export function PreviewPane({ fieldMetas }: PreviewPaneProps) {
  const { context, fieldOptions, range, sort, settings } = useAppState();
  const { gate } = useBilling();

  const checkedFields = useMemo(
    () => fieldOptions.filter((f) => f.checked).sort((a, b) => a.order - b.order),
    [fieldOptions],
  );
  const checkedFieldIds = useMemo(() => checkedFields.map((f) => f.id), [checkedFields]);

  const fetchFieldIds = useMemo(() => {
    if (!sort.fieldId || checkedFieldIds.includes(sort.fieldId)) return checkedFieldIds;
    return [...checkedFieldIds, sort.fieldId];
  }, [checkedFieldIds, sort.fieldId]);

  const [rows, setRows] = useState<PrintRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'word' | 'pdf' | null>(null);
  const cancelTokenRef = useRef(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const refresh = useCallback(async () => {
    if (!context) return;
    if (fetchFieldIds.length === 0) {
      setRows([]);
      setError(null);
      return;
    }
    const token = ++cancelTokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const fetched = await fetchRows(context, fetchFieldIds, range);
      if (token !== cancelTokenRef.current) return;
      setRows(fetched);
    } catch (err) {
      if (token !== cancelTokenRef.current) return;
      setError(describeError(err));
    } finally {
      if (token === cancelTokenRef.current) setLoading(false);
    }
  }, [context, fetchFieldIds, range]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sortedRows = useMemo(
    () => sortRows(rows, fieldMetas, sort.fieldId, sort.direction),
    [rows, fieldMetas, sort.fieldId, sort.direction],
  );

  const html = useMemo(
    () =>
      buildPrintHtml({
        fields: checkedFields,
        fieldMetas,
        rows: sortedRows,
        settings,
      }),
    [checkedFields, fieldMetas, sortedRows, settings],
  );

  const printingRef = useRef(false);
  const handlePrint = useCallback(async () => {
    // 双击「打印」会扣两次额度、弹两个打印对话框
    if (printingRef.current) return;
    if (!html) {
      notification.error({ message: '打印失败', description: '预览尚未就绪，请稍后再试。' });
      return;
    }
    printingRef.current = true;
    try {
      if (!(await gate())) return;
      printHtmlViaIframe(html);
    } catch (err) {
      notification.error({
        message: '打印失败',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      // 留一点时间给打印对话框弹出，别让连点在这一瞬间穿过去
      setTimeout(() => {
        printingRef.current = false;
      }, 800);
    }
  }, [html, gate]);

  const handleExportWord = useCallback(async () => {
    if (!context) return;
    if (!(await gate())) return;
    setExporting('word');
    try {
      const tableName = await getTableName(context.tableId).catch(() => '导出');
      const { blob, fileName } = await exportToWord({
        fields: checkedFields,
        fieldMetas,
        rows: sortedRows,
        settings,
        tableName,
      });
      downloadBlob(blob, fileName);
      notification.success({ message: 'Word 已导出', description: fileName, duration: 4 });
    } catch (err) {
      notification.error({
        message: 'Word 导出失败',
        description: describeError(err),
      });
    } finally {
      setExporting(null);
    }
  }, [context, checkedFields, fieldMetas, sortedRows, settings, gate]);

  const handleExportPdf = useCallback(async () => {
    if (!context) return;
    // 先确认预览真的就绪，再扣额度 —— 以前顺序反过来，
    // 用户在预览没渲染完时点一下，白白花掉一次免费额度还什么都拿不到
    const iframe = iframeRef.current;
    const body = iframe?.contentDocument?.body;
    if (!iframe || !body) {
      notification.error({
        message: 'PDF 导出失败',
        description: '预览尚未就绪，请稍后再试。',
      });
      return;
    }
    if (!(await gate())) return;
    setExporting('pdf');
    try {
      const tableName = await getTableName(context.tableId).catch(() => '导出');
      const fileName = buildPdfFileName(tableName);
      await exportToPdf({
        source: body,
        paperSize: settings.paperSize,
        orientation: settings.orientation,
        marginMm: settings.marginMm,
        fileName,
      });
      notification.success({ message: 'PDF 已导出', description: fileName, duration: 4 });
    } catch (err) {
      notification.error({
        message: 'PDF 导出失败',
        description: describeError(err),
      });
    } finally {
      setExporting(null);
    }
  }, [context, settings, gate]);

  const noFields = checkedFields.length === 0;
  const noRows = sortedRows.length === 0;
  const exportDisabled = noFields || noRows || loading || exporting !== null;

  return (
    <div className={styles.panel}>
      <div className={styles.toolbar}>
        <Space size={6} wrap>
          <Button
            type="primary"
            size="small"
            icon={<PrinterOutlined />}
            onClick={() => void handlePrint()}
            disabled={exportDisabled}
          >
            打印
          </Button>
          <Button
            size="small"
            icon={<FileWordOutlined />}
            onClick={() => void handleExportWord()}
            disabled={exportDisabled}
            loading={exporting === 'word'}
          >
            导出 Word
          </Button>
          <Button
            size="small"
            icon={<FilePdfOutlined />}
            onClick={() => void handleExportPdf()}
            disabled={exportDisabled}
            loading={exporting === 'pdf'}
          >
            导出 PDF
          </Button>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => void refresh()}
            loading={loading}
            disabled={!context}
          >
            刷新
          </Button>
        </Space>
        <Typography.Text className={styles.count} type="secondary">
          {loading ? (
            <Spin size="small" />
          ) : (
            <>
              {sortedRows.length} 条记录 · {checkedFields.length} 列
            </>
          )}
        </Typography.Text>
      </div>

      {error && (
        <Alert type="error" showIcon message="预览加载失败" description={error} />
      )}

      <div className={styles.frame}>
        {noFields ? (
          <div className={styles.empty}>请在「字段」中至少勾选一列</div>
        ) : (
          <iframe
            ref={iframeRef}
            className={styles.iframe}
            srcDoc={html}
            title="打印预览"
            sandbox="allow-same-origin"
          />
        )}
      </div>

      <Typography.Text className={styles.hint} type="secondary">
        <Tag color="default" style={{ marginInlineEnd: 4 }}>
          提示
        </Tag>
        打印通过浏览器对话框完成；Word/PDF 直接下载到本地。所有数据处理都在浏览器内。
      </Typography.Text>
    </div>
  );
}
